import {Box, Flex, Image, showMessage, Text, Title} from 'exsportia-components'
import {ModalWindowProps} from 'exsportia-components/dist/components/layouts/modal'
import React, {FC, useCallback, useEffect, useMemo, useState} from 'react'
import {useDispatch} from 'react-redux'
import {NavigateFunction, useNavigate, useParams} from 'react-router-dom'

import {
  AppearanceTemplateElement,
  AppearanceTemplateElementType,
  AppearanceTemplateType,
} from '../../../api/display-settings/types'
import {ServiceTypeOption} from '../../../api/service/types'
import {InstructorCard} from '../../../components/instructor-card/instructor-card'
import {PrivateCodeModal} from '../../../components/private-code-modal/private-code-modal'
import {SubscriptionCard} from '../../../components/subscription-card/subscription-card'
import {TranslatorScope} from '../../../locale/types'
import {checkServicePasscode} from '../../../services/booking/actions'
import {useBookingLoader} from '../../../services/booking/hooks'
import {useCheckSubscriptionPasscode} from '../../../services/booking/use-check-subscription-passcode'
import {useServiceOptions} from '../../../services/service-options/hooks'
import {Button} from '../../../ui/components/button/button'
import {Chevron} from '../../../ui/components/chevron/chevron'
import {EntityCard} from '../../../ui/components/entity-card/entity-card'
import {LocaleOptions, useLocale} from '../../../utils/i18n'
import {isNilOrEmpty, isNotNilAndNotEmpty} from '../../../utils/logic'
import {useModalActions} from '../../../utils/modals'
import {validationTokensMap} from '../../select-service/select-service-page'
import {StoreProductItem} from './store-product-item'
import {SubscriptionTile} from './subscription-tile'

const entityTypes: Record<
  Exclude<AppearanceTemplateElementType, 'LIST'>,
  Exclude<AppearanceTemplateElementType, 'LIST'>
> = {
  CLASS: 'CLASS',
  SPACE: 'SPACE',
  TEACHER: 'TEACHER',
  SUBPAGE: 'SUBPAGE',
  SERVICE: 'SERVICE',
  SUBSCRIPTION: 'SUBSCRIPTION',
  TEXT: 'TEXT',
  PRODUCT: 'PRODUCT',
}

const ShowScheduleButton: FC<{
  text?: string
  items?: AppearanceTemplateElement[]
}> = ({text, items = []}) => {
  const {l} = useLocale()
  const navigate = useNavigate()
  const classesIdsForSchedule: string[] = []
  items.forEach((item) => {
    if (item.type === 'CLASS' && item.data?.uid) {
      classesIdsForSchedule.push(item.data.uid)
    }
  })
  const clickHandler = useCallback(() => {
    navigate('class/schedule/select-session', {state: {classesIds: classesIdsForSchedule}})
  }, [classesIdsForSchedule])
  if (isNilOrEmpty(classesIdsForSchedule)) {
    return null
  }
  return (
    <Button
      type='flat_short'
      onClick={clickHandler}
    >
      {text || l('show-schedule', {caseAction: 'capitalize'})}
    </Button>
  )
}

export type LayoutByAppearanceTemplateProps = {
  collapsed?: boolean
  collapsible?: boolean
  setBackAction: React.Dispatch<React.SetStateAction<(() => void) | null>>
  collapseThreshold?: number
  appearanceTemplate: AppearanceTemplateType
  setShowFullScheduleButton?: React.Dispatch<React.SetStateAction<boolean>>
  isMemberBlocked?: boolean
  // Card presentation for a single-kind section — the store grid for products,
  // pass cards for subscriptions; null lists rows.
  cardKind?: SectionCardKind
  // CAROUSEL: one horizontal rail of the same cards instead of a wrapping grid.
  rail?: boolean
  // The section's column count (2/3/4, server #2577) — desktop card width;
  // mobile always fits two across.
  gridColumns?: number
}

// [mobile, desktop] card widths for a section's column count. The gutter
// subtractions pair with the grid's gap='16px'.
const cardWidthByColumns = (columns?: number): [string, string] => {
  if (columns === 2) {
    return ['calc(50% - 8px)', 'calc(50% - 8px)']
  }
  if (columns === 4) {
    return ['calc(50% - 8px)', 'calc(25% - 12px)']
  }
  return ['calc(50% - 8px)', 'calc(33.333% - 11px)']
}

export type ItemDataType = {
  name: string
  logo?: {secure_url?: string}
  sportType?: string
  teachersIds?: string[]
  profileImage?: {secure_url?: string}
  private?: boolean
  permissionData?: {
    reason?: string
    validated: boolean
  }
}

export type ComponentProps = {
  l: (scope: TranslatorScope, options?: LocaleOptions) => string
  item: AppearanceTemplateElement
  onClick: (appearanceTemplate: AppearanceTemplateType) => void
  navigate: NavigateFunction
  openModal: (modalConfig: ModalWindowProps) => void
  getServiceConfigByServiceType: (serviceType?: string | undefined) => ServiceTypeOption
  openServicePasscodeCheckModal: (serviceId: string | undefined, isNeedSelectTeacher: boolean, isSpace: boolean) => void
  openSubscriptionPasscodeCheckModal: (params: {subscriptionId: string; onSuccess?: () => void; onReject?: () => void}) => void
  isMemberBlocked?: boolean
  // Until the service-type options resolve, getServiceConfigByServiceType returns
  // DEFAULT_SERVICE_OPTION for every type — and its icon is a shopping cart. That
  // painted a cart on every class and space on each load, then swapped it for the
  // real sport icon. Skeleton the row instead of showing the wrong thing.
  serviceOptionsLoading?: boolean
  // The venue whose page this is. A card whose entity belongs to another venue (a group venue's
  // page showcasing a child's class) must open in THAT venue's booking flow — Organizations v1, WBA-6.
  currentVenueId?: string
}

// './class/x' stays relative on the entity's own venue; on any other page it becomes '/<venue>/class/x'.
const inEntityVenue = (item: AppearanceTemplateElement, currentVenueId: string | undefined, relative: string) => {
  const entityVenueId = item.data?.venueId
  if (!entityVenueId || !currentVenueId || entityVenueId === currentVenueId) {
 return relative
}
  return `/${entityVenueId}/${relative.replace(/^\.\//, '')}`
}

const serviceNavigateionMap = {
  instructorService: (entityId: string) => `./instructor-service/${entityId}/select-instructor/`,
  service: (entityId: string) => `./service/${entityId}/select-playspace/`,
  space: (entityId: string) => `./space/${entityId}/select-playspace/`,
}

const navComponentsMap: Record<string, FC<ComponentProps> | undefined> = {
  [entityTypes.SERVICE]: ({item, navigate, getServiceConfigByServiceType, l, openServicePasscodeCheckModal, isMemberBlocked, serviceOptionsLoading, currentVenueId}) => {
    const itemData = item.data as ItemDataType
    const isDisabled = isMemberBlocked || !itemData.permissionData?.validated
    return (
      <EntityCard
        text={itemData.name}
        icon={getServiceConfigByServiceType(itemData.sportType).icon}
        isLoading={serviceOptionsLoading}
        disabled={isDisabled}
        styleType='service'
        onClick={() =>
          itemData.private
            ? openServicePasscodeCheckModal(item.entityId, isNotNilAndNotEmpty(itemData.teachersIds), false)
            : isNotNilAndNotEmpty(itemData.teachersIds)
            ? navigate(inEntityVenue(item, currentVenueId, `./instructor-service/${item.entityId}/select-instructor/`))
            : navigate(inEntityVenue(item, currentVenueId, `./service/${item.entityId}/select-playspace/`))
        }
        subText={
          !isMemberBlocked && itemData.permissionData?.reason
            ? l(validationTokensMap[itemData.permissionData.reason as keyof typeof validationTokensMap], {
                caseAction: 'capitalize',
              })
            : null
        }
      />
    )
  },
  [entityTypes.CLASS]: ({item, navigate, getServiceConfigByServiceType, l, isMemberBlocked, serviceOptionsLoading, currentVenueId}) => {
    const itemData = item.data as ItemDataType
    const isDisabled = isMemberBlocked || !itemData.permissionData?.validated
    return (
      <EntityCard
        text={itemData.name}
        styleType='class'
        onClick={() => navigate(inEntityVenue(item, currentVenueId, `./class/${item.entityId}/select-session/`))}
        icon={getServiceConfigByServiceType(itemData.sportType).icon}
        isLoading={serviceOptionsLoading}
        disabled={isDisabled}
        subText={
          !isMemberBlocked && itemData.permissionData?.reason
            ? l(validationTokensMap[itemData.permissionData.reason as keyof typeof validationTokensMap], {
                caseAction: 'capitalize',
              })
            : null
        }
      />
    )
  },
  [entityTypes.SPACE]: ({item, navigate, getServiceConfigByServiceType, l, openServicePasscodeCheckModal, isMemberBlocked, serviceOptionsLoading, currentVenueId}) => {
    const itemData = item.data as ItemDataType
    const isDisabled = isMemberBlocked || !itemData.permissionData?.validated
    return (
      <EntityCard
        text={itemData.name}
        icon={getServiceConfigByServiceType(itemData.sportType).icon}
        isLoading={serviceOptionsLoading}
        onClick={() =>
          itemData.private
            ? openServicePasscodeCheckModal(item.entityId, false, true)
            : navigate(inEntityVenue(item, currentVenueId, `./space/${item.entityId}/select-playspace/`))
        }
        disabled={isDisabled}
        styleType='justTheSpace'
        subText={
          !isMemberBlocked && itemData.permissionData?.reason
            ? l(validationTokensMap[itemData.permissionData.reason as keyof typeof validationTokensMap], {
                caseAction: 'capitalize',
              })
            : null
        }
      />
    )
  },
  [entityTypes.TEACHER]: ({item, navigate, isMemberBlocked, currentVenueId}) => {
    const itemData = item.data as ItemDataType
    return (
      <Flex
        width='100%'
        cursor={isMemberBlocked ? 'default' : 'pointer'}
        onClick={isMemberBlocked ? undefined : () => navigate(inEntityVenue(item, currentVenueId, `./instructor/${item.entityId}/select-service`))}
      >
        <InstructorCard
          name={itemData.name}
          image={
            // `size` is not a prop on this Image — it feeds styled-system
            // width/height, leaving object-fit: fill, so faces came out squashed.
            <Image
              width='100%'
              height='100%'
              style={{objectFit: 'cover'}}
              src={itemData.profileImage?.secure_url || ''}
            />
          }
        />
      </Flex>
    )
  },
  [entityTypes.SUBSCRIPTION]: ({item, navigate, l, isMemberBlocked, currentVenueId}) => {
    const itemData = item.data as ItemDataType
    const unvalidated = isMemberBlocked || !itemData.permissionData?.validated
    const reason = itemData.permissionData?.reason as keyof typeof validationTokensMap | undefined
    return (
      <Flex
        width='100%'
        cursor={unvalidated ? 'default' : 'pointer'}
        onClick={
          unvalidated
            ? null
            : () => navigate(inEntityVenue(item, currentVenueId, `./subscription/${item.entityId}`))
        }
      >
        <SubscriptionCard
          name={itemData.name}
          subText={!isMemberBlocked && unvalidated && reason ? l(validationTokensMap[reason]) : null}
          imageUrl={itemData.logo?.secure_url || null}
        />
      </Flex>
    )
  },
  [entityTypes.SUBPAGE]: ({item, onClick, isMemberBlocked}) => {
    const filled = item.buttonStyle === 'FILLED'
    const color = item.buttonColor || undefined
    // No venue styling — the exact control every subpage rendered before.
    if (!filled && !color) {
      return (
        <Flex
          mt='8px'
          width='100%'
        >
          <Button
            // @ts-ignore
            onClick={isMemberBlocked ? undefined : onClick}
            type='secondary'
            disabled={isMemberBlocked}
          >
            {item.title}
          </Button>
        </Flex>
      )
    }
    // Venue-styled control (D199): Filled paints the picked colour; Simple
    // keeps a ghost outline in it.
    const accent = color ?? '#16191D'
    return (
      <Flex
        mt='8px'
        width='100%'
      >
        <Flex
          width='100%'
          p='12px 16px'
          alignItems='center'
          justifyContent='center'
          borderRadius='12px'
          cursor={isMemberBlocked ? 'default' : 'pointer'}
          onClick={isMemberBlocked ? undefined : onClick}
          backgroundColor={filled ? accent : 'transparent'}
          border={filled ? 'none' : `1.5px solid ${accent}`}
          opacity={isMemberBlocked ? 0.5 : 1}
        >
          <Text
            text={item.title}
            settings={{fontWeight: 600, color: filled ? '#FFFFFF' : accent}}
          />
        </Flex>
      </Flex>
    )
  },
  [entityTypes.PRODUCT]: ({item, navigate, l, isMemberBlocked}) => (
    <StoreProductItem
      l={l}
      item={item}
      navigate={navigate}
      isMemberBlocked={isMemberBlocked}
    />
  ),
  [entityTypes.TEXT]: ({item}) => {
    if (!item.entity?.goLive) {
      return null
    }
    if (item.entity.style === 'description') {
      return (<Box whiteSpace='pre-line'>{item.entity.text}</Box>)
    }
    return (
      <Title
        styleType={(item.entity.style.toLowerCase() || 'h3') as 'h1' | 'h2' | 'h3' | 'h4'}
        text={item.entity.text}
      />
    )
  },
}

// Whether an element will actually paint anything — used to drop unknown types
// instead of crashing (WBA-4516: admin can emit AREA/TITLE, which had no
// renderer entry) and to keep collapse thresholds honest.
const isRenderableElement = (element?: AppearanceTemplateElement): boolean => {
  if (!element || element.show === false) {
    return false
  }
  if (element.type === 'LIST') {
    // A section's own heading and description ARE content: venues write notices
    // as an item-less section ("IMPORTANT - PLEASE READ BEFORE BOOKING"). 4516
    // treated a section as renderable only through its children, which silently
    // dropped every such notice. The server has already stripped items that are
    // not live or not permitted, so an empty `items` here cannot be told apart
    // from a deliberately empty one — the heading decides, as it did pre-4516.
    return isNotNilAndNotEmpty(element.title)
      || isNotNilAndNotEmpty(element.description)
      || (element.items || []).some((child) => isRenderableElement(child))
  }
  if (element.type === 'TEXT') {
    return Boolean(element.entity?.goLive)
  }
  if (element.type === 'SUBPAGE') {
    return isNotNilAndNotEmpty(element.items)
  }
  return Boolean(navComponentsMap[element.type])
}

// How many of a section's items will actually paint — the only count a collapse
// threshold may be compared against.
const renderableCount = (items?: AppearanceTemplateElement[]): number =>
  (items || []).filter(isRenderableElement).length

// A section whose every painting child is one card-capable type (give or take
// TEXT decoration).
const sectionOnlyHolds = (
  items: AppearanceTemplateElement[] | undefined,
  type: AppearanceTemplateElementType,
): boolean => {
  const renderable = (items || []).filter(isRenderableElement)
  return renderable.some((child) => child.type === type)
    && renderable.every((child) => child.type === type || child.type === 'TEXT')
}

export type SectionCardKind = 'products' | 'subscriptions' | null

// The venue's explicit layout choice wins; a doc from before the vocabulary
// existed (no `layout`) keeps the products-only-means-cards heuristic.
// Subscriptions only become cards on an explicit Grid/Carousel — their rows
// are the long-standing default. Kinds without a card renderer always list.
const sectionCardKind = (element: AppearanceTemplateElement): SectionCardKind => {
  if (element.layout === 'LIST') {
    return null
  }
  if (sectionOnlyHolds(element.items, 'PRODUCT')) {
    return 'products'
  }
  const explicitCards = element.layout === 'GRID' || element.layout === 'CAROUSEL'
  if (explicitCards && sectionOnlyHolds(element.items, 'SUBSCRIPTION')) {
    return 'subscriptions'
  }
  return null
}

const sectionIsRail = (element: AppearanceTemplateElement): boolean =>
  element.layout === 'CAROUSEL' && sectionCardKind(element) !== null

// Escapes the page's 600px column to span the viewport as a band. The box is
// centered in the viewport, so the 50% shift lands on the screen edges.
const fullWidthBandStyle: React.CSSProperties = {
  width: '100vw',
  position: 'relative',
  left: '50%',
  transform: 'translateX(-50%)',
  background: '#FFFFFF',
}

// Off on WBA for now (Dima 2026-08-30). A 100vw band inside a venue page whose content sits in a
// centred card reads as a section that has fallen out of the card: it crosses the venue's own
// background image on both sides, and the cards in it start left of where every other section
// starts. A carousel that needs more room than the column scrolls inside the column instead.
// The venue's `fullWidth` choice is still stored and still honoured by the builder preview — this
// only stops WBA acting on it, so turning it back on is one flag.
// exsportia-components' Box and Flex support `overflow` and nothing axis-specific — the string
// "overflowX" does not appear anywhere in the built package — so an `overflowX`/`overflowY` PROP is
// dropped in silence. Passed through `style`, which does reach the DOM (Dima 2026-08-30).
// The rail had therefore never scrolled: with flexWrap nowrap it simply spilled its cards out of
// the venue card.
const railScrollStyle: React.CSSProperties = {overflowX: 'auto'}

const SECTION_BANDS: {enabled: boolean} = {enabled: false}

const bandStyle = (element: AppearanceTemplateElement): React.CSSProperties | undefined =>
  (SECTION_BANDS.enabled && element.fullWidth ? fullWidthBandStyle : undefined)

// TODO: fix recursive component usage
// eslint-disable-next-line
export const LayoutByAppearanceTemplate: FC<LayoutByAppearanceTemplateProps> = ({
  collapsible = false,
  // isRefetching,
  setBackAction,
  collapseThreshold = 3,
  appearanceTemplate,
  setShowFullScheduleButton,
  isMemberBlocked,
  cardKind = null,
  rail = false,
  gridColumns,
}) => {
  const {l} = useLocale()
  const navigate = useNavigate()
  const {venueId: currentVenueId} = useParams<{venueId: string}>()
  const dispatch = useDispatch()
  const {setIsLoading} = useBookingLoader()
  const {openModal, closeModal} = useModalActions()
  const {getServiceConfigByServiceType, isLoading: serviceOptionsLoading} = useServiceOptions()
  const {openSubscriptionPasscodeCheckModal} = useCheckSubscriptionPasscode()
  const [appearanceTemplateToUse, setAppearanceTemplateToUse] = useState<AppearanceTemplateType>(appearanceTemplate)
  const [showSectionSchedule, setShowSectionSchedule] = useState<boolean>(false)
  useEffect(() => {
    setAppearanceTemplateToUse(appearanceTemplate)
  }, [appearanceTemplate])
  const [subPageCollapseConfig, setSubPageCollapseConfig] = useState<{
    isUsed: boolean
    collapsed: boolean
    collapsible: boolean
    collapseThreshold: number
    gridColumns?: number
    cardKind?: SectionCardKind
    rail?: boolean
  }>({
    isUsed: false,
    collapsed: true,
    collapsible: false,
    collapseThreshold: 0,
  })
  // The subpage swap replaces the template inside this same instance, so the
  // column count has to travel with it rather than trusting the prop.
  const gridColumnsToUse = subPageCollapseConfig.isUsed ? subPageCollapseConfig.gridColumns : gridColumns
  const cardKindToUse = subPageCollapseConfig.isUsed ? (subPageCollapseConfig.cardKind ?? null) : cardKind
  const railToUse = subPageCollapseConfig.isUsed ? Boolean(subPageCollapseConfig.rail) : rail
  const [collapsed, setCollapsed] = useState<boolean>(
    subPageCollapseConfig.isUsed ? subPageCollapseConfig.collapsed : true,
  )
  const collapseConfigToUse = useMemo(() => {
    if (subPageCollapseConfig.isUsed) {
      return subPageCollapseConfig
    }
    return {
      collapsible,
      collapseThreshold,
    }
  }, [collapseThreshold, collapsible, subPageCollapseConfig])
  const [title, setTitle] = useState<string>('')
  const [description, setDescription] = useState<string | null>(null)
  const openServicePasscodeCheckModal = useCallback(
    (serviceId: string | undefined, isNeedSelectTeacher: boolean, isSpace: boolean) => {
      if (!serviceId) {
        return
      }
      const handleCheckPassCode = (passCode: string) => {
        setIsLoading(true)
        dispatch(
          checkServicePasscode({
            password: passCode,
            sportId: serviceId,
            callback: () => {
              closeModal()
              showMessage(l('success'), 'success')
              navigate(
                isSpace
                  ? serviceNavigateionMap.space(serviceId)
                  : isNeedSelectTeacher
                  ? serviceNavigateionMap.instructorService(serviceId)
                  : serviceNavigateionMap.service(serviceId),
              )
              setIsLoading(false)
            },
            rejectCallback: () => {
              setIsLoading(false)
              showMessage(l('error'), 'error')
            },
          }),
        )
      }
      openModal({
        title: l('privateService'),
        component: <PrivateCodeModal handleCheckCode={handleCheckPassCode} />,
        footerConfig: {
          disabled: true,
        },
      })
    },
    [dispatch, setIsLoading, closeModal, l, navigate],
  )
  useEffect(() => {
    if (appearanceTemplate !== appearanceTemplateToUse) {
      return setBackAction(() => () => {
        setTitle('')
        setDescription(null)
        setShowSectionSchedule(false)
        setAppearanceTemplateToUse(appearanceTemplate)
        setSubPageCollapseConfig({isUsed: false, collapsed: true, collapsible: false, collapseThreshold: 0})
        if (typeof setShowFullScheduleButton === 'function') {
          setShowFullScheduleButton(true)
        }
      })
    }
    setBackAction(null)
  }, [appearanceTemplateToUse, appearanceTemplate])
  // Bounded by the actual item count (a threshold beyond the list used to push
  // `undefined` into the render array), and hiddenCount is a pure derivation —
  // the old setState-inside-useMemo caused render-phase writes and stale counts.
  // The threshold counts what actually paints. Slicing the raw list let an
  // element that renders nothing (a goLive:false text item, an empty subpage)
  // eat a visible slot, so a section whose leading items were hidden collapsed
  // to an empty box behind a Show-more button.
  const renderableTemplate = useMemo(
    () => appearanceTemplateToUse.filter(isRenderableElement),
    [appearanceTemplateToUse],
  )
  const appearanceTemplateWithCollapsed = useMemo(() => {
    if (collapseConfigToUse.collapsible && collapsed) {
      return renderableTemplate.slice(0, Math.max(0, collapseConfigToUse.collapseThreshold))
    }
    return renderableTemplate
  }, [renderableTemplate, collapsed, collapseConfigToUse])
  const hiddenCount = Math.max(0, renderableTemplate.length - appearanceTemplateWithCollapsed.length)

  return (
    <>
      {title && (
        <Flex
          px='16px'
          justifyContent='space-between'
        >
          <Title
            text={title}
            styleType='h2'
          />
          {showSectionSchedule && !isMemberBlocked && <ShowScheduleButton items={appearanceTemplateToUse} />}
        </Flex>
      )}
      {description && (
        <Flex pl='16px'>
          <Text text={description} settings={{croppedLines: 0, whiteSpace: 'pre-line'}} />
        </Flex>
      )}
      {/* A product-only section is the store in miniature: the same card grid,
          not a column of rows (Dima 2026-08-19). Collapse slicing above applies
          to both shapes. The subpage view swaps the template inside this same
          instance, so the flag is re-derived rather than trusted from props. */}
      {Boolean(cardKindToUse) && (
        <Flex
          px='16px'
          gap={railToUse ? '12px' : '16px'}
          flexWrap={railToUse ? 'nowrap' : 'wrap'}
          style={railToUse ? railScrollStyle : undefined}
          pb={railToUse ? '8px' : undefined}
          width='100%'
          maxWidth='100%'
          {...(railToUse ? {minWidth: 0} : {})}
        >
          {appearanceTemplateWithCollapsed.filter(isRenderableElement).map((element, index) => {
            if (element.type === 'TEXT') {
              const TextComponent = navComponentsMap[entityTypes.TEXT]
              if (!TextComponent) {
                return null
              }
              return (
                <Box
                  key={`product-card-${index}`}
                  width='100%'
                >
                  <TextComponent
                    l={l}
                    item={element}
                    navigate={navigate}
                    openModal={openModal}
                    onClick={() => undefined}
                    getServiceConfigByServiceType={getServiceConfigByServiceType}
                    openServicePasscodeCheckModal={openServicePasscodeCheckModal}
                    openSubscriptionPasscodeCheckModal={openSubscriptionPasscodeCheckModal}
                    isMemberBlocked={isMemberBlocked}
                  />
                </Box>
              )
            }
            const cardBody = element.type === 'SUBSCRIPTION' ? (
              <SubscriptionTile
                l={l}
                item={element}
                navigate={navigate}
                isMemberBlocked={isMemberBlocked}
                cardWidth={railToUse ? '100%' : cardWidthByColumns(gridColumnsToUse)}
              />
            ) : (
              <StoreProductItem
                l={l}
                item={element}
                navigate={navigate}
                isMemberBlocked={isMemberBlocked}
                variant='card'
                cardWidth={railToUse ? '100%' : cardWidthByColumns(gridColumnsToUse)}
              />
            )
            if (railToUse) {
              return (
                <Box
                  key={`section-card-${index}`}
                  flex='none'
                  width={element.type === 'SUBSCRIPTION' ? ['240px', '260px'] : ['170px', '200px']}
                >
                  {cardBody}
                </Box>
              )
            }
            return <React.Fragment key={`section-card-${index}`}>{cardBody}</React.Fragment>
          })}
        </Flex>
      )}
      {!cardKindToUse
        && appearanceTemplateWithCollapsed.map((element, index) => {
        if (!isRenderableElement(element)) {
          return null
        }
        if (element.type === 'LIST') {
          return (
            <Box
              key={`fragment-${index}`}
              style={bandStyle(element)}
            >
              <Flex
                mb='16px'
                mx='16px'
                gap='12px'
                flexDirection='column'
              >
                <Flex justifyContent='space-between'>
                  <Title
                    styleType='h2'
                    text={element.title}
                  />
                  {element.schedule && !isMemberBlocked && <ShowScheduleButton items={element.items} />}
                </Flex>
                {element.description && (
                  <Text
                    text={element.description}
                    settings={{croppedLines: 0, whiteSpace: 'pre-line'}}
                  />
                )}
              </Flex>
              <LayoutByAppearanceTemplate
                key={`element-${index}`}
                appearanceTemplate={element.items || []}
                collapsible={element.collapsible && renderableCount(element.items) > element.collapseThreshold}
                setBackAction={setBackAction}
                collapseThreshold={element.collapseThreshold}
                isMemberBlocked={isMemberBlocked}
                cardKind={sectionCardKind(element)}
                rail={sectionIsRail(element)}
                gridColumns={element.columns}
              />
            </Box>
          )
        }
        const Component = navComponentsMap[element.type]
        if (!Component) {
          // eslint-disable-next-line no-console
          console.warn(`LayoutByAppearanceTemplate: no renderer for section item type "${element.type}"`)
          return null
        }
        const isSubpageButton = element.type === 'SUBPAGE'
        const isNotNav = isSubpageButton || element.type === 'TEXT'
        return (
          <Flex
            py='8px'
            px='16px'
            width='100%'
            alignItems='center'
            justifyContent='space-between'
            key={`navigationElement${index}`}
            backgroundHover={isNotNav ? 'unset' : 'rgba(0, 16, 61, 0.06)'}
          >
            <Component
              l={l}
              item={element}
              currentVenueId={currentVenueId}
              navigate={navigate}
              getServiceConfigByServiceType={getServiceConfigByServiceType}
              serviceOptionsLoading={serviceOptionsLoading}
              openServicePasscodeCheckModal={openServicePasscodeCheckModal}
              openSubscriptionPasscodeCheckModal={openSubscriptionPasscodeCheckModal}
              isMemberBlocked={isMemberBlocked}
              onClick={() => {
                setTitle(element.title)
                setDescription(element.description || null)
                setShowSectionSchedule(element.schedule || false)
                if (isNotNilAndNotEmpty(element.items)) {
                  setAppearanceTemplateToUse(element.items)
                }
                if (typeof setShowFullScheduleButton === 'function') {
                  setShowFullScheduleButton(false)
                }
                setSubPageCollapseConfig({
                  isUsed: true,
                  collapsed: true,
                  collapseThreshold: element.collapseThreshold,
                  collapsible: element.collapsible && renderableCount(element.items) > element.collapseThreshold,
                  gridColumns: element.columns,
                  cardKind: sectionCardKind(element),
                  rail: sectionIsRail(element),
                })
              }}
              openModal={openModal}
            />
            {!isNotNav && (
              <Chevron
                styleType='simple'
                direction={isMemberBlocked || (element.data as ItemDataType).permissionData?.validated === false ? 'block' : 'right'}
              />
            )}
          </Flex>
        )
      })}
      {collapseConfigToUse.collapsible && (hiddenCount > 0 || !collapsed) && (
        <Flex
          px='16px'
          mt='8px'
        >
          <Button
            type='secondary'
            onClick={() => setCollapsed((prev) => !prev)}
          >
            {collapsed ? `${l('showMore')} (${hiddenCount})` : l('showLess')}
          </Button>
        </Flex>
      )}
    </>
  )
}
